import type { CSSProperties } from 'react';
import { cn } from '@/lib/cn';
import { flavorVar, type Flavor } from './flavors';

type WaveVariant = 'roll' | 'bump' | 'drip';

/** viewBox is 0 0 1440 120; the flat side sits along y=120, the crest points up. */
const PATHS: Record<WaveVariant, string> = {
  roll: 'M0,64 C240,120 480,8 720,48 C960,88 1200,28 1440,72 L1440,120 L0,120 Z',
  bump: 'M0,104 C360,20 1080,20 1440,104 L1440,120 L0,120 Z',
  drip: 'M0,44 C120,44 120,96 240,96 C360,96 360,44 480,44 C600,44 600,96 720,96 C840,96 840,44 960,44 C1080,44 1080,96 1200,96 C1320,96 1320,44 1440,44 L1440,120 L0,120 Z',
};

interface WaveProps {
  flavor?: Flavor;
  /** Use the pale wash of the flavor instead of the saturated hue. */
  shade?: 'base' | 'soft';
  /** Escape hatch for a non-flavor fill, e.g. `"var(--color-canvas)"`. */
  color?: string;
  /**
   * Which edge of the parent (a `relative` element) the wave hugs. `bottom`
   * fills a band inside the section's lower edge; `top` sits just above the
   * section's top edge. Either way the crest points up unless `invert`.
   */
  position?: 'top' | 'bottom';
  variant?: WaveVariant;
  /** Mirror horizontally. */
  flip?: boolean;
  /** Flip vertically so the crest points down. */
  invert?: boolean;
  /** Visible height of the wave band. Default 64. */
  height?: number;
  /** Slow horizontal sway (frozen under reduced motion). */
  drift?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * A decorative SVG wave that bleeds one section's colour into the next — the
 * house alternative to a hard divider rule. Drop it as a child of a
 * `position: relative` section:
 *
 *   <section className="relative bg-canvas">
 *     …content…
 *     <Wave position="bottom" flavor="melon" />   // melon section comes next
 *   </section>
 */
export function Wave({
  flavor = 'berry',
  shade = 'base',
  color,
  position = 'bottom',
  variant = 'roll',
  flip = false,
  invert = false,
  height = 64,
  drift = false,
  className,
  style,
}: WaveProps) {
  const transform =
    [invert ? 'scaleY(-1)' : '', flip ? 'scaleX(-1)' : ''].filter(Boolean).join(' ') || undefined;

  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-x-0 z-10 overflow-hidden',
        position === 'top' ? 'top-0 -translate-y-[99%]' : 'bottom-0',
        className,
      )}
      style={{ height, ...style }}
    >
      <svg
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        className={cn('block h-full', drift && 'wave-drift')}
        style={{
          fill: color ?? flavorVar(flavor, shade === 'soft' ? 'soft' : undefined),
          transform,
          width: drift ? '120%' : '100%',
          marginLeft: drift ? '-10%' : undefined,
          animationName: drift ? 'wave-drift' : undefined,
          animationDuration: drift ? '9s' : undefined,
          animationIterationCount: drift ? 'infinite' : undefined,
          animationTimingFunction: drift ? 'ease-in-out' : undefined,
        }}
      >
        <path d={PATHS[variant]} />
      </svg>
    </div>
  );
}

interface WaveStackProps {
  /** Back-to-front. Two or three colours. */
  flavors: Flavor[];
  position?: 'top' | 'bottom';
  /** Height of the front-most wave; each layer behind peeks ~16px higher. */
  height?: number;
  className?: string;
}

/**
 * Two or three offset {@link Wave} layers for depth — used along the bottom edge
 * of the top wave-menu, or under a hero.
 */
export function WaveStack({
  flavors,
  position = 'bottom',
  height = 72,
  className,
}: WaveStackProps) {
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-x-0 z-10',
        position === 'top' ? 'top-0' : 'bottom-0',
        className,
      )}
    >
      {flavors.map((flavor, i) => (
        <Wave
          key={`${flavor}-${i}`}
          flavor={flavor}
          position={position}
          variant={i === flavors.length - 1 ? 'roll' : 'bump'}
          flip={i % 2 === 1}
          height={height + (flavors.length - 1 - i) * 16}
        />
      ))}
    </div>
  );
}
